import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Row, Col, Spinner, Nav, NavItem, NavLink, Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';
import DateRangeSelector from '../Page/Reporting/DateRangeSelector';
import { formatDateTimeForAPI } from '../constants/utils';

/** Default range mirrors Reporting: the current month, up to end of today. */
const thisMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  return { start: start.toISOString(), end: end.toISOString() };
};

// Single-hue for magnitude bars; validated blue/orange pair for the 2-series budget view.
const HUE = '#3B82C4';
const HUE_APPROVED = '#3B82C4';
const HUE_DELIVERED = '#E08A3C';

/** A labelled magnitude bar row (accessible: label + numeric value as text, bar shows magnitude). */
const BarRow = ({ label, value, max, display, color = HUE }) => {
  const pct = max > 0 ? Math.max(3, Math.round((value / max) * 100)) : 0;
  return (
    <div className="d-flex align-items-center gap-2 mb-2">
      <div style={{ width: 150 }} className="text-truncate small" title={label}>{label}</div>
      <div className="flex-grow-1 bg-light rounded" style={{ height: 16 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4 }} />
      </div>
      <div style={{ width: 90 }} className="text-end small fw-medium">{display ?? value}</div>
    </div>
  );
};

const StatTile = ({ label, value, icon, tone = 'primary' }) => (
  <Card className="mb-0 h-100">
    <CardBody className="d-flex align-items-center gap-3">
      <div className={`bg-${tone} bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center`} style={{ width: 46, height: 46, minWidth: 46 }}>
        <FeatherIcon icon={icon} size={22} className={`text-${tone}`} />
      </div>
      <div>
        <div className="fs-4 fw-bold text-dark">{value}</div>
        <div className="text-muted small">{label}</div>
      </div>
    </CardBody>
  </Card>
);

const ProcurementAnalytics = () => {
  const { t } = useTranslation();
  const [view, setView] = useState('pipeline');
  const [range, setRange] = useState(thisMonthRange);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [pipeline, setPipeline] = useState(null);
  const [budget, setBudget] = useState(null);
  const [vendor, setVendor] = useState(null);

  const params = useCallback(() => {
    const p = {};
    if (range.start) p.from = formatDateTimeForAPI(range.start);
    if (range.end) p.to = formatDateTimeForAPI(range.end);
    return p;
  }, [range]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (view === 'pipeline') {
        const { data } = await api.get('procurement-analytics/request-pipeline', { params: params() });
        setPipeline(data?.response || null);
      } else if (view === 'budget') {
        const { data } = await api.get('procurement-analytics/budget', { params: { ...params(), period } });
        setBudget(data?.response || null);
      } else {
        const { data } = await api.get('procurement-analytics/vendor-spend', { params: params() });
        setVendor(data?.response || null);
      }
    } catch (e) { console.error('analytics error', e); } finally { setLoading(false); }
  }, [view, params, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCsv = async () => {
    const map = { pipeline: 'request-pipeline', budget: 'budget', vendor: 'vendor-spend' };
    const p = new URLSearchParams(params());
    if (view === 'budget') p.append('period', period);
    try {
      const res = await api.get(`procurement-analytics/${map[view]}/export-csv?${p.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `${map[view]}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 8000);
    } catch (e) { console.error('csv error', e); }
  };

  const money = (v, cur) => `${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur || ''}`;
  const statusLabel = (s) => t(`planning_purchasing.status.${s.toLowerCase()}`, s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' '));

  const VIEWS = [
    { key: 'pipeline', label: t('planning_purchasing.view_pipeline', 'Request Pipeline'), icon: 'trending-up' },
    { key: 'budget', label: t('planning_purchasing.view_budget', 'Budget'), icon: 'dollar-sign' },
    { key: 'vendor', label: t('planning_purchasing.view_vendor', 'Vendor Spend'), icon: 'briefcase' },
  ];

  return (
    <div>
      {/* View tabs */}
      <Nav pills className="gap-2 mb-3 flex-wrap">
        {VIEWS.map((v) => (
          <NavItem key={v.key}>
            <NavLink style={{ cursor: 'pointer' }} className={`tab-btn ${view === v.key ? 'active' : ''}`} onClick={() => setView(v.key)}>
              <FeatherIcon icon={v.icon} size={15} className="me-1" />{v.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      {/* Filters — same preset range picker as Reporting */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <DateRangeSelector
          startDate={range.start}
          endDate={range.end}
          onChange={(start, end) => setRange({ start, end })}
        />
        {view === 'budget' && (
          <select className="form-select form-select-sm" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ maxWidth: 160 }}>
            <option value="monthly">{t('planning_purchasing.monthly', 'Monthly')}</option>
            <option value="quarterly">{t('planning_purchasing.quarterly', 'Quarterly')}</option>
            <option value="annual">{t('planning_purchasing.annual', 'Annual')}</option>
          </select>
        )}
        <button className="btn btn-outline-secondary btn-sm ms-auto" onClick={exportCsv}>
          <FeatherIcon icon="download" size={14} className="me-1" />{t('planning_purchasing.export_csv', 'Export CSV')}
        </button>
      </div>

      {loading && <div className="text-center py-4"><Spinner color="primary" /></div>}

      {/* Request Pipeline */}
      {!loading && view === 'pipeline' && pipeline && (
        <>
          <Row className="g-3 mb-3">
            <Col md={4}><StatTile icon="inbox" tone="primary" value={pipeline.totalRequests} label={t('planning_purchasing.total_requests', 'Total requests')} /></Col>
            <Col md={4}><StatTile icon="clock" tone="info" value={pipeline.avgTimeToResolutionHours != null ? `${pipeline.avgTimeToResolutionHours} h` : '—'} label={t('planning_purchasing.avg_resolution', 'Avg. time to resolution')} /></Col>
            <Col md={4}><StatTile icon="alert-triangle" tone="warning" value={pipeline.urgentPending} label={t('planning_purchasing.urgent_pending', 'Urgent pending')} /></Col>
          </Row>
          <Row className="g-3">
            <Col md={6}><Card className="h-100"><CardBody>
              <h6 className="text-primary mb-3">{t('planning_purchasing.by_status', 'By status')}</h6>
              {Object.entries(pipeline.byStatus || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <BarRow key={k} label={statusLabel(k)} value={v} max={Math.max(...Object.values(pipeline.byStatus))} />
              ))}
              {Object.keys(pipeline.byStatus || {}).length === 0 && <div className="text-muted small">{t('planning_purchasing.no_data', 'No data')}</div>}
            </CardBody></Card></Col>
            <Col md={6}><Card className="h-100"><CardBody>
              <h6 className="text-primary mb-3">{t('planning_purchasing.by_type', 'By type')}</h6>
              {Object.entries(pipeline.byType || {}).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <BarRow key={k} label={k === 'ASSET' ? t('planning_purchasing.type_asset', 'Asset') : t('planning_purchasing.type_parts_consumables', 'Parts & Consumables')} value={v} max={Math.max(...Object.values(pipeline.byType))} />
              ))}
              {Object.keys(pipeline.byType || {}).length === 0 && <div className="text-muted small">{t('planning_purchasing.no_data', 'No data')}</div>}
            </CardBody></Card></Col>
            <Col md={12}><Card><CardBody>
              <h6 className="text-primary mb-3">{t('planning_purchasing.by_location', 'By location')}</h6>
              {(pipeline.byLocation || []).map((l) => (
                <BarRow key={l.locationId} label={l.locationName || `#${l.locationId}`} value={l.count} max={Math.max(...pipeline.byLocation.map((x) => x.count))} />
              ))}
              {(pipeline.byLocation || []).length === 0 && <div className="text-muted small">{t('planning_purchasing.no_data', 'No data')}</div>}
            </CardBody></Card></Col>
          </Row>
        </>
      )}

      {/* Budget */}
      {!loading && view === 'budget' && budget && (
        <>
          <Row className="g-3 mb-3">
            <Col md={6}><StatTile icon="check-circle" tone="primary" value={money(budget.totalApprovedPoValue, budget.currency)} label={t('planning_purchasing.approved_po_value', 'Approved PO value')} /></Col>
            <Col md={6}><StatTile icon="truck" tone="success" value={money(budget.totalDeliveredValue, budget.currency)} label={t('planning_purchasing.delivered_value', 'Delivered value')} /></Col>
          </Row>
          <Card><CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-primary mb-0">{t('planning_purchasing.value_by_period', 'Value by period')}</h6>
              <div className="d-flex gap-3 small">
                <span><span className="d-inline-block rounded me-1" style={{ width: 12, height: 12, background: HUE_APPROVED }} />{t('planning_purchasing.approved', 'Approved')}</span>
                <span><span className="d-inline-block rounded me-1" style={{ width: 12, height: 12, background: HUE_DELIVERED }} />{t('planning_purchasing.delivered', 'Delivered')}</span>
              </div>
            </div>
            {(budget.byPeriod || []).map((p) => {
              const max = Math.max(...budget.byPeriod.flatMap((x) => [Number(x.approvedValue), Number(x.deliveredValue)]), 1);
              return (
                <div key={p.period} className="mb-3">
                  <div className="small fw-medium mb-1">{p.period}</div>
                  <BarRow label={t('planning_purchasing.approved', 'Approved')} value={Number(p.approvedValue)} max={max} display={money(p.approvedValue, budget.currency)} color={HUE_APPROVED} />
                  <BarRow label={t('planning_purchasing.delivered', 'Delivered')} value={Number(p.deliveredValue)} max={max} display={money(p.deliveredValue, budget.currency)} color={HUE_DELIVERED} />
                </div>
              );
            })}
            {(budget.byPeriod || []).length === 0 && <div className="text-muted small">{t('planning_purchasing.no_data', 'No data')}</div>}
          </CardBody></Card>
        </>
      )}

      {/* Vendor Spend */}
      {!loading && view === 'vendor' && vendor && (
        <Row className="g-3">
          <Col md={7}><Card className="h-100"><CardBody>
            <h6 className="text-primary mb-3">{t('planning_purchasing.spend_by_vendor', 'Spend by vendor')}</h6>
            {(vendor.spendByVendor || []).map((s) => (
              <BarRow key={s.vendorName} label={s.vendorName} value={Number(s.total)} max={Math.max(...vendor.spendByVendor.map((x) => Number(x.total)), 1)} display={money(s.total, vendor.currency)} />
            ))}
            {(vendor.spendByVendor || []).length === 0 && <div className="text-muted small">{t('planning_purchasing.no_data', 'No data')}</div>}
          </CardBody></Card></Col>
          <Col md={5}><Card className="h-100"><CardBody>
            <h6 className="text-primary mb-3">{t('planning_purchasing.avg_lead_time', 'Avg. lead time (days)')}</h6>
            <Table size="sm" className="mb-3">
              <tbody>
                {(vendor.avgLeadTimeByVendor || []).map((l) => (
                  <tr key={l.vendorName}><td>{l.vendorName}</td><td className="text-end fw-medium">{l.avgDays}</td></tr>
                ))}
                {(vendor.avgLeadTimeByVendor || []).length === 0 && <tr><td className="text-muted small">{t('planning_purchasing.no_data', 'No data')}</td></tr>}
              </tbody>
            </Table>
            <h6 className="text-primary mb-2">{t('planning_purchasing.repeat_vendors', 'Repeat vendors')}</h6>
            <div className="d-flex flex-wrap gap-2">
              {(vendor.repeatVendors || []).map((r) => (
                <span key={r.vendorName} className="badge bg-light text-dark border">{r.vendorName} · {r.poCount}</span>
              ))}
              {(vendor.repeatVendors || []).length === 0 && <div className="text-muted small">{t('planning_purchasing.none', 'None')}</div>}
            </div>
          </CardBody></Card></Col>
        </Row>
      )}
    </div>
  );
};

export default ProcurementAnalytics;
